/**
 * Central light/dark theme state helpers — pure functions, no React.
 *
 * ONE module owns the storage key, the valid values, and the
 * localStorage/matchMedia/root-class mechanics. The provider
 * (`src/components/theme/theme-provider.tsx`) and the pre-paint IIFE in
 * `index.html` both build on these constants and must stay in sync with them.
 */

export type Theme = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

export const THEME_STORAGE_KEY = "theme";
export const THEME_DEFAULT: Theme = "dark";
export const VALID_THEMES: readonly Theme[] = ["dark", "light", "system"];

/**
 * Read the theme from `localStorage[THEME_STORAGE_KEY]`, validating the
 * stored value. Falls back to `THEME_DEFAULT` when storage is unavailable
 * or the stored value is not a valid theme.
 */
export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    return stored && (VALID_THEMES as readonly string[]).includes(stored)
      ? stored
      : THEME_DEFAULT;
  } catch {
    return THEME_DEFAULT;
  }
}

/** Current OS-level color scheme preference. */
export function getSystemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Resolve a (possibly "system") theme to the concrete light/dark mode. */
export function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? getSystemTheme() : theme;
}

/** Replace the `light`/`dark` class on `<html>` with the resolved theme. */
export function applyThemeClassToRoot(resolved: ResolvedTheme): void {
  const root = window.document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
}

/**
 * Subscribe to OS color-scheme changes. Calls `cb` with the new resolved
 * theme whenever the preference flips. Returns an unsubscribe function.
 */
export function subscribeToSystemTheme(
  cb: (resolved: ResolvedTheme) => void,
): () => void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => cb(mq.matches ? "dark" : "light");
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
